import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import {
  AppCacheStatus,
  AppDispatch,
  AppState,
  fullstorySelectors,
  MatchingUserAppCacheStatus,
  MatchingUsersActions,
  MatchingUsersAsyncActions,
  matchingUsersSelectors,
  searchMetaSelectors,
  SessionAsyncActions,
  SessionReplayActions,
  sessionReplaySelectors,
  useReleaseAlexaBackgroundRefs,
} from '@fullstory/redux';
import { EventSearch } from '@fullstory/proto/fs/api/search';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { SearchieMatchingUsersSettings, SortOrder } from '@fullstory/proto/fs/services/alexa';
import { Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { matchingUserStatusToAppCacheStatus } from '../utils';
import { useFlags } from '@fullstory/react-hooks';
import { UID } from '@fullstory/util/uid';

const { getOrgId } = fullstorySelectors;
const { getSearchCacheKey } = searchMetaSelectors;
const { getNumMatchingUsers, hasLoadedSuccessfully } = matchingUsersSelectors;
const {
  makeGetComputedAt,
  makeGetIsLoading,
  makeGetNumMatchingUsers,
  makeGetStatus,
  makeGetUsersResponse,
} = sessionReplaySelectors;

type SettingsProps = Pick<
  FetchUsersProps,
  'page' | 'pageSize' | 'sort' | 'userSegmentDefinitions' | 'eventSearch'
>;
const buildSettings = ({
  page,
  pageSize,
  sort,
  userSegmentDefinitions,
  eventSearch,
}: SettingsProps): SearchieMatchingUsersSettings => ({
  start: page * pageSize,
  limit: pageSize,
  sortOrder: sort,
  additionalSegmentDefinitions: userSegmentDefinitions ?? [],
  eventSearch: eventSearch ?? null,
  earliestInSession: false,
});

// Interface that the functional components are expected to match
interface FetchUsersArgs {
  onCacheSwap: () => void;
  onRefresh: () => void;
  status: AppCacheStatus;
  computedAt: timestamp | null;
}

export interface FetchUsersProps {
  segmentDefinition: SegmentDefinition;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  sort: SortOrder;
  userSegmentDefinitions?: SegmentDefinition[];
  sessionSegmentDefinitions?: SegmentDefinition[];
  eventSearch?: EventSearch;
  storageId?: string;
  shouldFetch?: boolean;
}

export const useFetchUsers = ({
  segmentDefinition,
  page,
  setPage,
  pageSize,
  sort,
  userSegmentDefinitions,
  sessionSegmentDefinitions,
  eventSearch,
  storageId,
  shouldFetch = true,
}: FetchUsersProps): FetchUsersArgs => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();

  const settings = useMemo(
    () =>
      buildSettings({
        page,
        pageSize,
        sort,
        userSegmentDefinitions,
        eventSearch,
      }),
    [page, pageSize, sort, eventSearch, userSegmentDefinitions],
  );

  const { getStatus, getNumUsers, getIsLoading, getComputedAt, getCurrentUsersResponse } = useMemo(
    () => ({
      getNumUsers: storageId ? makeGetNumMatchingUsers(storageId) : null,
      getIsLoading: storageId ? makeGetIsLoading(storageId) : null,
      getStatus: storageId ? makeGetStatus(storageId) : null,
      getComputedAt: storageId ? makeGetComputedAt(storageId) : null,
      getCurrentUsersResponse: storageId ? makeGetUsersResponse(storageId) : null,
    }),
    [storageId],
  );

  const { numMatchingUsers, allLoaded, searchCacheKey, status, computedAt, matchingUserStatus } =
    useSelector(
      (state: AppState) => ({
        numMatchingUsers: getNumUsers ? getNumUsers(state) : getNumMatchingUsers(state),
        allLoaded: getIsLoading ? !getIsLoading(state) : hasLoadedSuccessfully(state),
        status: getStatus
          ? getStatus(state)
          : matchingUserStatusToAppCacheStatus(state.matchingUsers.cachedMatchingUsers.status),
        computedAt: getComputedAt
          ? getComputedAt(state)
          : state.matchingUsers.cachedMatchingUsers.shownData?.computedAt ?? Timestamp.nil,
        searchCacheKey: getSearchCacheKey(state),
        matchingUserStatus: storageId ? null : state.matchingUsers.cachedMatchingUsers.status,
      }),
      shallowEqual,
    );

  useEffect(() => {
    if (!shouldFetch) return;

    let cancel: (() => void) | null = null;

    if (storageId) {
      const uid = UID();
      const appCache = SessionAsyncActions.appCache(flags.featureFlags().AppGravatarProfiles, uid);
      appCache.action(dispatch, storageId, {
        segmentDefinition,
        searchCacheKey,
        settings,
        sessionSegmentDefinitions,
        page,
        orgId,
        storageId,
        followUpReq: false,
      });
      cancel = () => appCache.cancel(dispatch, uid);
    } else {
      dispatch(
        MatchingUsersAsyncActions.fetch(
          segmentDefinition,
          searchCacheKey,
          settings,
          sessionSegmentDefinitions,
          page,
          orgId,
          false,
          false,
          flags.featureFlags().AppGravatarProfiles,
        ),
      );
    }

    return () => cancel?.();
  }, [
    flags,
    dispatch,
    segmentDefinition,
    searchCacheKey,
    settings,
    sessionSegmentDefinitions,
    page,
    orgId,
    storageId,
    shouldFetch,
  ]);

  useEffect(() => {
    if (matchingUserStatus === MatchingUserAppCacheStatus.middle && page === 0 && !storageId) {
      dispatch(
        MatchingUsersAsyncActions.fetch(
          segmentDefinition,
          searchCacheKey,
          settings,
          sessionSegmentDefinitions,
          page,
          orgId,
          true,
          true,
          flags.featureFlags().AppGravatarProfiles,
        ),
      );
    }
  }, [
    dispatch,
    matchingUserStatus,
    orgId,
    page,
    searchCacheKey,
    segmentDefinition,
    sessionSegmentDefinitions,
    settings,
    storageId,
    flags,
  ]);

  // If we've loaded data, and the page is beyond the results, reset to page 0
  useEffect(() => {
    if ((allLoaded && settings.start > numMatchingUsers) || settings.start < 0) {
      setPage(0);
    }
  }, [setPage, allLoaded, settings.start, numMatchingUsers]);

  const usersResponse = useSelector((state: AppState) =>
    getCurrentUsersResponse ? getCurrentUsersResponse(state) : null,
  );
  const backgroundRefIds = usersResponse?.backgroundRefId ? [usersResponse.backgroundRefId] : [];
  useReleaseAlexaBackgroundRefs(orgId, backgroundRefIds, !shouldFetch);

  return {
    onCacheSwap: () => {
      if (storageId) {
        dispatch(SessionReplayActions.appCacheSwap(storageId));
      } else {
        dispatch(MatchingUsersActions.fetchUsersCacheSwap());
        dispatch(MatchingUsersActions.timeRangeCacheSwap());
        dispatch(MatchingUsersActions.allTimeRangeCacheSwap());
      }
    },
    onRefresh: () => {
      if (storageId) {
        SessionAsyncActions.appCache(flags.featureFlags().AppGravatarProfiles).action(
          dispatch,
          storageId,
          {
            segmentDefinition,
            searchCacheKey,
            settings,
            sessionSegmentDefinitions,
            page,
            orgId,
            storageId,
            followUpReq: true,
          },
        );
      } else {
        dispatch(
          MatchingUsersAsyncActions.fetch(
            segmentDefinition,
            searchCacheKey,
            settings,
            sessionSegmentDefinitions,
            page,
            orgId,
            true,
            true,
            flags.featureFlags().AppGravatarProfiles,
          ),
        );
      }
    },
    status,
    computedAt,
  };
};
